/**
 * nj-schedule-zone: the zone a NEW event's schedule is read and written in (CC 0477).
 *   explicit (the organizer picked one, or the record already has one) → the venue's own zone → the workspace's →
 *   the viewer's display zone, last.
 * A date edit never pins the zone: until the organizer picks one explicitly, it follows the venue (and the workspace
 * zone when it arrives). When it moves, the typed wall clock is kept and re-expressed in the new zone.
 */
import { eventWallClock, zonedWallClockIso } from "../../lib/datetime";

/** The zone a saved location carries. The locations adapter exposes it as `tz`. */
export const venueZoneOf = (loc: unknown): string | null => {
  const l = (loc ?? null) as { tz?: unknown; timezone?: unknown; timeZone?: unknown } | null;
  const z = l ? (l.tz ?? l.timezone ?? l.timeZone) : null;
  return typeof z === "string" && z.trim() ? z : null;
};
export function scheduleZone(p: { explicit?: string | null; venue?: string | null; workspace?: string | null; display: string }): string {
  return p.explicit || p.venue || p.workspace || p.display;
}
/** The same wall clock in another zone ("09:00 Chicago" → "09:00 Los Angeles"). Empty stays empty. */
export function rezoneWallClock(iso: string | null | undefined, from: string, to: string): string {
  if (!iso) return "";
  const w = eventWallClock(iso, from);
  if (!w) return iso;
  const key = `${w.y}-${String(w.mo).padStart(2, "0")}-${String(w.d).padStart(2, "0")}`;
  return zonedWallClockIso(key, `${String(w.h).padStart(2, "0")}:${String(w.mi).padStart(2, "0")}`, to);
}

/** The derived zone last seen, and the record it was seen for (CC 0487). */
export interface ZoneSeen { key: string; zone: string }
/**
 * Whether the open record's times move to a new zone. Only when the zone moved FOR THE SAME RECORD, and never with an
 * explicit zone. The first sight of a record (no previous, or a different key: a document switch) re-baselines with
 * no shift, so a switched-to draft's saved times are never re-expressed from the previous draft's zone.
 */
export function zoneShift(prev: ZoneSeen | null, next: ZoneSeen, explicit: string | null | undefined): { from: string; to: string } | null {
  if (!prev || prev.key !== next.key) return null;
  if (explicit) return null;
  if (!prev.zone || !next.zone || prev.zone === next.zone) return null;
  return { from: prev.zone, to: next.zone };
}
